import { EffectLimit } from "../combat/effects.ts";
import { InputFlag } from "../protocol/input.ts";
import { TICK_MS } from "./step.ts";

/**
 * The run's rule (ADR-116 decisions 1 to 5): running is the walk with a longer step, and whether a step may be that
 * long is the map server's to say from its own state. The same pure functions serve the map server, which decides, and
 * the page, which predicts its own run from the state the server last told it. Integer maths, no allocation.
 */

/**
 * Run steps to a second, one a tick: what `run_drain`, in points a second, is counted against.
 */
export const DRAIN_STEPS_PER_SECOND = 1000 / TICK_MS;

/**
 * Whether a consumed input steps at the run's length (ADR-116 decision 3): it carries the sprint bit, and the
 * character is alive, free to move, outside a freeze's grace, holds Stamina and is not spent. Anything else walks; the
 * bit is otherwise ignored, with no refusal and nothing to probe. Whether the input has a direction, and whether the
 * movement budget lets it step, are the caller's.
 *
 * @param flags The input's `InputFlag` bits.
 * @param limits The `EffectLimit` bits in force on the character: a stun, a chain's root, a dash and a freeze's hold
 *   each forbid moving.
 * @param stamina The Stamina the character holds, in whole points.
 * @param spent Whether it ran itself empty and has not yet recovered to `run_restart` (`spentAfter`).
 * @param alive Whether it is alive.
 * @param grace Whether it is in a freeze's grace, which is walking and nothing else (ADR-056 decision 41).
 * @returns True when the step is a run step.
 * @example
 * mayRun(InputFlag.Sprint, 0, 100, false, true, false);
 * // true
 */
export function mayRun(flags: number, limits: number, stamina: number, spent: boolean, alive: boolean, grace: boolean): boolean {
  return (flags & InputFlag.Sprint) !== 0 && alive && !grace && !spent && stamina > 0 && (limits & EffectLimit.Move) === 0;
}

/**
 * Whether a character is spent after its Stamina changed (ADR-116 decision 5): it becomes spent at zero, stays spent
 * while below `restart`, and may run again once it holds `restart`. Without it a character holding the key at an empty
 * bar would run one step for each point it regained.
 *
 * @param spent Whether it was spent.
 * @param stamina The Stamina it holds now, in whole points.
 * @param restart The rules' `run_restart`.
 * @returns Whether it is spent now.
 */
export function spentAfter(spent: boolean, stamina: number, restart: number): boolean {
  if (stamina <= 0) {
    return true;
  }
  return spent && stamina < restart;
}

/**
 * The whole points a run step takes (ADR-116 decision 2): each run step adds `drain` to a counter kept per character
 * and takes one point for each `DRAIN_STEPS_PER_SECOND` it then holds, so a second of run steps takes exactly `drain`
 * points with no fraction. At 10 a second that is a point every second step.
 *
 * @param counter The character's counter before the step, 0 to `DRAIN_STEPS_PER_SECOND - 1`.
 * @param drain The rules' `run_drain`, points a second.
 * @returns The points this step takes.
 * @example
 * drainPoints(10, 10);
 * // 1
 */
export function drainPoints(counter: number, drain: number): number {
  return Math.floor((counter + drain) / DRAIN_STEPS_PER_SECOND);
}

/**
 * The drain's counter after a run step: what is left once the step's whole points are taken (`drainPoints`).
 *
 * @param counter The counter before the step, 0 to `DRAIN_STEPS_PER_SECOND - 1`.
 * @param drain The rules' `run_drain`, points a second.
 * @returns The counter after it, 0 to `DRAIN_STEPS_PER_SECOND - 1`.
 */
export function drainCounterAfter(counter: number, drain: number): number {
  return (counter + drain) % DRAIN_STEPS_PER_SECOND;
}
